import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "top-nav" }
const _hoisted_2 = { class: "nav-left" }
const _hoisted_3 = ["aria-expanded"]
const _hoisted_4 = {
  key: 0,
  class: "agents-dropdown-menu"
}
const _hoisted_5 = ["aria-disabled", "tabindex"]
const _hoisted_6 = { class: "nav-right" }
const _hoisted_7 = ["aria-expanded"]
const _hoisted_8 = {
  key: 0,
  class: "avatar-img"
}
const _hoisted_9 = ["src"]
const _hoisted_10 = {
  key: 1,
  class: "avatar-initials"
}
const _hoisted_11 = {
  key: 2,
  class: "avatar-icon"
}
const _hoisted_12 = {
  key: 0,
  class: "dropdown-menu",
  tabindex: "-1"
}
const _hoisted_13 = { class: "dropdown-header" }
const _hoisted_14 = { class: "dropdown-avatar" }
const _hoisted_15 = { key: 0 }
const _hoisted_16 = ["src"]
const _hoisted_17 = {
  key: 1,
  class: "avatar-initials"
}
const _hoisted_18 = {
  key: 2,
  class: "avatar-icon"
}
const _hoisted_19 = { class: "dropdown-user-info" }
const _hoisted_20 = { class: "dropdown-name" }
const _hoisted_21 = { class: "dropdown-email" }
const _hoisted_22 = ["title"]
const _hoisted_23 = {
  width: "22",
  height: "22",
  viewBox: "0 0 20 20",
  fill: "currentColor",
  style: {
                    transform: 'rotate(90deg)',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                  },
  "aria-hidden": "true"
}
const _hoisted_24 = { class: "dropdown-actions" }

import { ref, computed, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useAuth } from "@/composables/useAuth";
import { useMenuBehavior } from "@/composables/useMenuBehavior";
import { fetchStatus, fetchConfig } from "@/services/api";
import DevelopmentSmokeResultsModal from "@/components/DevelopmentSmokeResultsModal.vue";
import AddPanelModal from "@/components/AddPanelModal.vue";
import { runDevelopmentSmokeTest } from "@/composables/developmentSmokeTest";


export default /*@__PURE__*/_defineComponent({
  __name: 'TopNav',
  setup(__props) {

function getFirstScope(obj: unknown): string | undefined {
  if (!obj || typeof obj !== "object") return undefined;
  const o = obj as Record<string, unknown>;
  const scopes = o.scopes;
  if (Array.isArray(scopes) && scopes.length > 0 && typeof scopes[0] === "string") return scopes[0] as string;
  return undefined;
}

const router = useRouter();
const { isAuthenticated, user, login, logout } = useAuth();

// Detect whether current route is the login page
const isOnLogin = computed(() => router.currentRoute.value.name === "login");

// Menu behavior setup
const userMenuRef = ref<HTMLElement | null>(null);
const { isOpen: userMenuOpen, toggleMenu: toggleUserMenu, closeMenu: closeUserMenu } = useMenuBehavior({
  menuContainerRef: userMenuRef
});

// Agents menu behavior setup
const agentsMenuRef = ref<HTMLElement | null>(null);
const { isOpen: agentsMenuOpen, toggleMenu: toggleAgentsMenu, closeMenu: closeAgentsMenu } = useMenuBehavior({
  menuContainerRef: agentsMenuRef
});

// AddPanelModal state
const showAddPanelModal = ref(false);

// detect local dev based on NODE_ENV
const isDev = process.env.NODE_ENV === "development";

const status = ref<"pending" | "ok" | "error">("pending");

// development smoke test modal state
const showDevelopmentSmokeTestModal = ref(false);
const developmentSmokeTestResult = ref("");

function onUpdateDevelopmentSmokeOpen(v: boolean) {
  showDevelopmentSmokeTestModal.value = v;
}

function onCloseDevelopmentSmokeModal() {
  showDevelopmentSmokeTestModal.value = false;
}

async function runDevelopmentSmokeTestHandler() {
  developmentSmokeTestResult.value = "Running...";
  showDevelopmentSmokeTestModal.value = true;
  // Prefer server-provided config for validation
  try {
    const cfg = await fetchConfig();
    const firstScope = getFirstScope(cfg);
    developmentSmokeTestResult.value = await runDevelopmentSmokeTest({
      expectedIss: `https://login.microsoftonline.com/${cfg.tenantId}`,
      expectedAud: cfg.clientId,
      expectedScope: firstScope,
    });
  } catch (e) {
    // fallback to build-time envs
    developmentSmokeTestResult.value = await runDevelopmentSmokeTest({
      expectedIss: process.env.VUE_APP_AUTHORITY || process.env.VUE_APP_AUTH_ISSUER,
      expectedAud: process.env.VUE_APP_API_CLIENT_ID || process.env.VUE_APP_API_AUDIENCE,
      expectedScope: process.env.VUE_APP_API_SCOPE,
    });
  }
}

async function checkStatusOnce() {
  status.value = "pending";
  try {
    const result = await fetchStatus();
    status.value = result === "ok" ? "ok" : "error";
  } catch {
    status.value = "error";
  }
}

// Override toggleUserMenu to include status check
function handleToggleUserMenu() {
  toggleUserMenu();
  if (userMenuOpen.value) {
    checkStatusOnce();
    nextTick(() => {
      // Focus the dropdown for accessibility
      const el = document.querySelector(".dropdown-menu");
      if (el) (el as HTMLElement).focus();
    });
  }
}

function onSettings() {

  router.push({ name: "settings" });
  // Close the user menu via the composable (avoid referencing undefined `menuOpen`)
  closeUserMenu();
}

// Agents menu functions
function handleToggleAgentsMenu() {
  toggleAgentsMenu();
}

function handleCreateAgent() {
  closeAgentsMenu();
  // Navigate to home first, then show the dialog
  router.push({ name: "home" }).then(() => {
    showAddPanelModal.value = true;
  });
}

function closeAddPanelModal() {
  showAddPanelModal.value = false;
}

const userDisplayName = computed(() => user.value?.name || user.value?.username || "User");
const userEmail = computed(() => user.value?.username || "");
const userPicture = computed<string | null>(() => {
  const pic = user.value?.idTokenClaims?.picture;
  return typeof pic === "string" ? pic : null;
});
const userInitials = computed(() => {
  const name = user.value?.name || "";
  if (!name) return null;
  const parts = name.split(" ");
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || null;
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
});

const statusColor = computed(() => {
  if (status.value === "ok") return "#27c840";
  if (status.value === "pending") return "#bbb";
  return "#c00";
});
const statusTooltip = computed(() => {
  if (status.value === "ok") return "Connected";
  if (status.value === "pending") return "Checking...";
  return "Not connected";
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("nav", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", {
          class: "agents-menu-wrapper",
          ref_key: "agentsMenuRef",
          ref: agentsMenuRef
        }, [
          _createElementVNode("button", {
            class: "agents-button",
            onClick: handleToggleAgentsMenu,
            "aria-haspopup": "true",
            "aria-expanded": _unref(agentsMenuOpen)
          }, [
            _cache[3] || (_cache[3] = _createTextVNode(" Agents ")),
            (_openBlock(), _createElementBlock("svg", {
              width: "12",
              height: "12",
              viewBox: "0 0 12 12",
              fill: "currentColor",
              class: _normalizeClass(["dropdown-icon", { rotated: _unref(agentsMenuOpen) }])
            }, _cache[2] || (_cache[2] = [
              _createElementVNode("path", { d: "M2 4l4 4 4-4H2z" }, null, -1)
            ]), 2))
          ], 8, _hoisted_3),
          (_unref(agentsMenuOpen))
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createVNode(_component_router_link, {
                  to: { name: 'home' },
                  class: _normalizeClass(["dropdown-item", { disabled: isOnLogin.value }]),
                  onClick: _unref(closeAgentsMenu),
                  "active-class": "router-link-active",
                  "exact-active-class": "router-link-exact-active",
                  "aria-disabled": isOnLogin.value ? 'true' : 'false'
                }, {
                  default: _withCtx(() => _cache[4] || (_cache[4] = [
                    _createElementVNode("span", { class: "dropdown-label" }, "Home", -1)
                  ])),
                  _: 1,
                  __: [4]
                }, 8, ["class", "onClick", "aria-disabled"]),
                _createVNode(_component_router_link, {
                  to: { name: 'settings' },
                  class: _normalizeClass(["dropdown-item", { disabled: isOnLogin.value }]),
                  onClick: _unref(closeAgentsMenu),
                  "active-class": "router-link-active",
                  "exact-active-class": "router-link-exact-active",
                  "aria-disabled": isOnLogin.value ? 'true' : 'false'
                }, {
                  default: _withCtx(() => _cache[5] || (_cache[5] = [
                    _createElementVNode("span", { class: "dropdown-label" }, "Settings", -1)
                  ])),
                  _: 1,
                  __: [5]
                }, 8, ["class", "onClick", "aria-disabled"]),
                _cache[7] || (_cache[7] = _createElementVNode("div", {
                  class: "dropdown-divider",
                  "aria-hidden": "true"
                }, null, -1)),
                _createElementVNode("button", {
                  class: _normalizeClass(["dropdown-item dropdown-button", { disabled: isOnLogin.value }]),
                  onClick: handleCreateAgent,
                  "aria-disabled": isOnLogin.value ? 'true' : 'false',
                  tabindex: isOnLogin.value ? -1 : 0
                }, _cache[6] || (_cache[6] = [
                  _createElementVNode("span", { class: "dropdown-label" }, "Create Agent", -1)
                ]), 10, _hoisted_5)
              ]))
            : _createCommentVNode("", true)
        ], 512)
      ]),
      _createElementVNode("div", _hoisted_6, [
        (!_unref(isAuthenticated))
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(login) && _unref(login)(...args)))
            }, "Sign In"))
          : (_openBlock(), _createElementBlock("div", {
              key: 1,
              class: "user-menu-wrapper",
              ref_key: "userMenuRef",
              ref: userMenuRef
            }, [
              _createElementVNode("button", {
                class: "avatar-btn",
                onClick: handleToggleUserMenu,
                "aria-haspopup": "true",
                "aria-expanded": _unref(userMenuOpen)
              }, [
                (userPicture.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                      _createElementVNode("img", {
                        src: userPicture.value,
                        alt: "User avatar"
                      }, null, 8, _hoisted_9)
                    ]))
                  : (userInitials.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(userInitials.value), 1))
                    : (_openBlock(), _createElementBlock("span", _hoisted_11, "👤"))
              ], 8, _hoisted_7),
              (_unref(userMenuOpen))
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, [
                        (userPicture.value)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_15, [
                              _createElementVNode("img", {
                                src: userPicture.value,
                                alt: "User avatar"
                              }, null, 8, _hoisted_16)
                            ]))
                          : (userInitials.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(userInitials.value), 1))
                            : (_openBlock(), _createElementBlock("span", _hoisted_18, "👤"))
                      ]),
                      _createElementVNode("div", _hoisted_19, [
                        _createElementVNode("div", _hoisted_20, _toDisplayString(userDisplayName.value), 1),
                        _createElementVNode("div", _hoisted_21, _toDisplayString(userEmail.value), 1),
                        _createElementVNode("span", {
                          class: "status-indicator",
                          title: statusTooltip.value,
                          style: _normalizeStyle({
                  color: statusColor.value,
                  marginLeft: 0,
                  marginTop: '0.5em',
                  fontSize: '1.2em',
                }),
                          "aria-label": "Connection status"
                        }, [
                          (_openBlock(), _createElementBlock("svg", _hoisted_23, _cache[8] || (_cache[8] = [
                            _createElementVNode("path", { d: "M10 0L3 11h5v9l7-11h-5z" }, null, -1)
                          ])))
                        ], 12, _hoisted_22)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_24, [
                      _createElementVNode("button", { onClick: onSettings }, "Settings"),
                      isDev
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            onClick: runDevelopmentSmokeTestHandler
                          }, "Run Auth Smoke Test"))
                        : _createCommentVNode("", true),
                      _createElementVNode("button", {
                        onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(logout) && _unref(logout)(...args)))
                      }, "Sign Out")
                    ])
                  ]))
                : _createCommentVNode("", true)
            ], 512))
      ])
    ]),
    _createVNode(DevelopmentSmokeResultsModal, {
      open: showDevelopmentSmokeTestModal.value,
      result: developmentSmokeTestResult.value,
      "onUpdate:open": onUpdateDevelopmentSmokeOpen,
      onClose: onCloseDevelopmentSmokeModal
    }, null, 8, ["open", "result"]),
    (showAddPanelModal.value)
      ? (_openBlock(), _createBlock(AddPanelModal, {
          key: 0,
          onClose: closeAddPanelModal
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})